import { useCallback } from 'react';
import { DefaultValue, atom, useRecoilValue, useResetRecoilState, useSetRecoilState, } from 'recoil';
import { z } from 'zod';
import { safeRetrieve, safeStore } from '../utils/storage';
const ScheduledBroadcast = z.object({
    id: z.string().uuid(),
    startsAt: z.number().int().positive(),
    name: z.string().min(1).max(68),
    streamId: z.string(),
    aesKey: z.string().length(64),
    publicKey: z.string().length(66),
});
export function validateBroadcast(broadcast) {
    try {
        ScheduledBroadcast.parse(broadcast);
    }
    catch (error) {
        if (error instanceof z.ZodError) {
            return error.issues;
        }
    }
    return [];
}
const StoredScheduleState = safeRetrieve('scheduledBroadcasts', (rawBroadcasts) => {
    try {
        const broadcasts = z.array(ScheduledBroadcast).parse(rawBroadcasts);
        const broadcastsByStreamId = {};
        for (const broadcast of broadcasts) {
            broadcastsByStreamId[broadcast.streamId] = broadcast;
        }
        return {
            broadcasts,
            broadcastsByStreamId,
        };
    }
    catch (_ignored) {
        return {
            broadcasts: [],
            broadcastsByStreamId: {},
        };
    }
});
const scheduleState = atom({
    key: 'scheduleState',
    default: StoredScheduleState || {
        broadcasts: [],
        broadcastsByStreamId: {},
    },
    effects: [
        ({ onSet }) => {
            onSet((newValue) => {
                if (newValue instanceof DefaultValue ||
                    newValue.broadcasts.length === 0) {
                    safeStore('scheduledBroadcasts', undefined);
                }
                else {
                    safeStore('scheduledBroadcasts', newValue.broadcasts);
                }
            });
        },
    ],
});
export function useScheduledBroadcasts() {
    return useRecoilValue(scheduleState).broadcasts;
}
export function useScheduledBroadcast(streamId) {
    const { broadcastsByStreamId } = useRecoilValue(scheduleState);
    return !streamId ? undefined : broadcastsByStreamId[streamId];
}
export function useUpdateScheduledBroadcast() {
    const setState = useSetRecoilState(scheduleState);
    return useCallback((broadcastId, updater) => {
        setState((prev) => {
            const { broadcasts } = prev;
            const newBroadcasts = [];
            const newBroadcastsByStreamId = {};
            for (const broadcast of broadcasts) {
                const newBroadcast = broadcast.id !== broadcastId
                    ? broadcast
                    : updater(broadcast);
                if (!newBroadcast) {
                    continue;
                }
                newBroadcasts.push(newBroadcast);
                newBroadcastsByStreamId[newBroadcast.streamId] =
                    newBroadcast;
            }
            newBroadcasts.sort((a, b) => a.startsAt - b.startsAt);
            return {
                broadcasts: newBroadcasts,
                broadcastsByStreamId: newBroadcastsByStreamId,
            };
        });
    }, [setState]);
}
export function useDeleteScheduledBroadcast() {
    const updateScheduledBroadcast = useUpdateScheduledBroadcast();
    return useCallback((broadcastId) => {
        updateScheduledBroadcast(broadcastId, () => undefined);
    }, [updateScheduledBroadcast]);
}
export function useClearScheduledBroadcasts() {
    return useResetRecoilState(scheduleState);
}
export function useAddScheduledBroadcast() {
    const setState = useSetRecoilState(scheduleState);
    return useCallback((broadcast) => {
        setState(({ broadcasts, broadcastsByStreamId }) => ({
            broadcasts: [...broadcasts, broadcast],
            broadcastsByStreamId: {
                ...broadcastsByStreamId,
                [broadcast.streamId]: broadcast,
            },
        }));
    }, [setState]);
}
export function buildScheduledBroadcast(partial) {
    return {
        id: generateBroadcastId(),
        ...partial,
    };
}
function generateBroadcastId() {
    try {
        return crypto.randomUUID();
    }
    catch (_) {
        // Pass it through.
    }
    /**
     * Fallback to a random UUIDv4-like string. This is a fallback for insecure
     * environments like… using the dev instance via LAN.
     */
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8; // UUID v4 spec for 'y'
        return v.toString(16);
    });
}
